import { create } from 'zustand';

export function aiExecutionKey(
  workspaceId: string,
  triggerKey: string,
  resourceType: string,
  resourceId: string,
) {
  return `${workspaceId}:${triggerKey}:${resourceType}:${resourceId}`;
}

export type AiTriggerRunState = {
  executionId: string;
  status: 'pending' | 'running';
  triggerKey: string;
  resourceType: string;
  resourceId: string;
  requestedByUserId?: string;
};

type BootstrapRow = {
  id: string;
  triggerKey: string;
  resourceType: string;
  resourceId: string;
  status: string;
  requestedByUserId: string;
};

type BootstrapScope = {
  resourceType: string;
  resourceId: string;
};

interface AiTriggerExecutionState {
  byKey: Record<string, AiTriggerRunState>;
  publicationAiContentByPublicationId: Record<string, string>;
  setRunning: (workspaceId: string, state: AiTriggerRunState) => void;
  clear: (
    workspaceId: string,
    triggerKey: string,
    resourceType: string,
    resourceId: string,
  ) => void;
  bootstrap: (
    workspaceId: string,
    rows: BootstrapRow[],
    scope?: BootstrapScope,
  ) => void;
  setPublicationAiContent: (publicationId: string, content: string) => void;
  clearPublicationAiContent: (publicationId: string) => void;
}

export const useAiTriggerExecutionStore = create<AiTriggerExecutionState>(
  (set) => ({
    byKey: {},
    publicationAiContentByPublicationId: {},
    setRunning: (workspaceId, run) =>
      set((s) => ({
        byKey: {
          ...s.byKey,
          [aiExecutionKey(
            workspaceId,
            run.triggerKey,
            run.resourceType,
            run.resourceId,
          )]: run,
        },
      })),
    clear: (workspaceId, triggerKey, resourceType, resourceId) =>
      set((s) => {
        const k = aiExecutionKey(
          workspaceId,
          triggerKey,
          resourceType,
          resourceId,
        );
        const next = { ...s.byKey };
        delete next[k];
        return { byKey: next };
      }),
    bootstrap: (workspaceId, rows, scope) =>
      set((s) => {
        const next = { ...s.byKey };
        if (scope) {
          const prefix = `${workspaceId}:`;
          const scopedSuffix = `:${scope.resourceType}:${scope.resourceId}`;
          for (const k of Object.keys(next)) {
            if (k.startsWith(prefix) && k.endsWith(scopedSuffix)) {
              delete next[k];
            }
          }
        }
        for (const row of rows) {
          if (row.status === 'pending' || row.status === 'running') {
            next[
              aiExecutionKey(
                workspaceId,
                row.triggerKey,
                row.resourceType,
                row.resourceId,
              )
            ] = {
              executionId: row.id,
              status: row.status as 'pending' | 'running',
              triggerKey: row.triggerKey,
              resourceType: row.resourceType,
              resourceId: row.resourceId,
              requestedByUserId: row.requestedByUserId,
            };
          }
        }
        return { byKey: next };
      }),
    setPublicationAiContent: (publicationId, content) =>
      set((s) => ({
        publicationAiContentByPublicationId: {
          ...s.publicationAiContentByPublicationId,
          [publicationId]: content,
        },
      })),
    clearPublicationAiContent: (publicationId) =>
      set((s) => {
        const copy = { ...s.publicationAiContentByPublicationId };
        delete copy[publicationId];
        return { publicationAiContentByPublicationId: copy };
      }),
  }),
);

export function selectAiTriggerRunning(
  workspaceId: string | undefined,
  triggerKey: string,
  resourceType: string,
  resourceId: string,
) {
  return (s: AiTriggerExecutionState) => {
    if (!workspaceId) return null;
    return (
      s.byKey[
        aiExecutionKey(workspaceId, triggerKey, resourceType, resourceId)
      ] ?? null
    );
  };
}
